import React from 'react';
import { StatsTabProps } from './types';

const StatsTab: React.FC<StatsTabProps> = ({
  tasks,
  categories,
  statsFilter,
  setStatsFilter,
  statsDateFrom,
  setStatsDateFrom,
  statsDateTo,
  setStatsDateTo
}) => {
  // Фильтрация выполненных задач (включая архивные)
  const filteredTasks = tasks.filter(task => {
    if (task.status !== 'done' && task.status !== 'archived') return false;
    // Используем completedAt если есть, иначе fallback на updatedAt
    const dateToUse = task.completedAt || task.updatedAt;
    if (!dateToUse) return false;
    
    if (statsFilter === 'all') return true;
    
    const taskDate = new Date(dateToUse);
    const taskDateOnly = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());
    const dateFrom = statsDateFrom ? new Date(statsDateFrom + 'T00:00:00') : null;
    const dateTo = statsDateTo ? new Date(statsDateTo + 'T23:59:59') : null;
    
    if (dateFrom && taskDateOnly < dateFrom) return false;
    if (dateTo && taskDateOnly > dateTo) return false;
    
    return true;
  });

  // Определяем формат и создаём ВСЕ периоды в диапазоне
  let allPeriods: string[] = [];
  let dateFormat: 'hour' | 'day' | 'month' | 'year' = 'day';
  
  if (statsFilter === 'day') {
    dateFormat = 'hour';
    const today = statsDateFrom || new Date().toISOString().split('T')[0];
    for (let h = 0; h < 24; h++) {
      allPeriods.push(`${today}-${String(h).padStart(2, '0')}`);
    }
  } else if (statsFilter === 'month') {
    dateFormat = 'day';
    const dateFrom = new Date(statsDateFrom + 'T00:00:00');
    const dateTo = new Date(statsDateTo + 'T00:00:00');
    
    let currentDate = new Date(dateFrom);
    while (currentDate <= dateTo) {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      allPeriods.push(`${year}-${month}-${day}`);
      currentDate.setDate(currentDate.getDate() + 1);
    }
  } else if (statsFilter === 'year') {
    dateFormat = 'month';
    const year = new Date(statsDateFrom).getFullYear();
    for (let m = 1; m <= 12; m++) {
      allPeriods.push(`${year}-${String(m).padStart(2, '0')}`);
    }
  } else {
    dateFormat = 'year';
    if (filteredTasks.length > 0) {
      const dates = filteredTasks.map(t => new Date(t.completedAt || t.updatedAt!));
      const minYear = Math.min(...dates.map(d => d.getFullYear()));
      const maxYear = Math.max(...dates.map(d => d.getFullYear()));
      
      for (let y = minYear; y <= maxYear; y++) {
        allPeriods.push(String(y));
      }
    } else {
      allPeriods.push(String(new Date().getFullYear()));
    }
  }
  
  // Группировка задач по периодам
  const tasksByPeriod: { [key: string]: number } = {};
  
  allPeriods.forEach(period => {
    tasksByPeriod[period] = 0;
  });
  
  filteredTasks.forEach(task => {
    const dateToUse = task.completedAt || task.updatedAt!;
    const date = new Date(dateToUse);
    let periodKey: string;
    
    if (dateFormat === 'hour') {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      periodKey = `${year}-${month}-${day}-${hour}`;
    } else if (dateFormat === 'day') {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      periodKey = `${year}-${month}-${day}`;
    } else if (dateFormat === 'month') {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      periodKey = `${year}-${month}`;
    } else {
      periodKey = String(date.getFullYear());
    }
    
    if (tasksByPeriod.hasOwnProperty(periodKey)) {
      tasksByPeriod[periodKey]++;
    }
  });

  const maxCount = Math.max(...Object.values(tasksByPeriod), 1);

  // Статистика по категориям
  const categoryStats: { [key: string]: { count: number; name: string; color: string; icon: string } } = {};
  let uncategorizedCount = 0;
  
  filteredTasks.forEach(task => {
    if (task.category) {
      const catId = task.category.id.toString();
      if (!categoryStats[catId]) {
        categoryStats[catId] = {
          count: 0,
          name: task.category.name,
          color: task.category.color,
          icon: task.category.icon
        };
      }
      categoryStats[catId].count++;
    } else {
      uncategorizedCount++;
    }
  });

  if (uncategorizedCount > 0) {
    categoryStats['uncategorized'] = {
      count: uncategorizedCount,
      name: 'Без категории',
      color: '#9ca3af',
      icon: '📝'
    };
  }

  const totalTasks = filteredTasks.length;
  const categoriesArray = Object.values(categoryStats).sort((a, b) => b.count - a.count);

  return (
    <div>
      {/* Фильтры */}
      <div style={{
        background: '#f9fafb',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Фильтры</h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' }}>
          <button
            onClick={() => setStatsFilter('all')}
            style={{
              padding: '8px 16px',
              background: statsFilter === 'all' ? '#3b82f6' : 'white',
              color: statsFilter === 'all' ? 'white' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: statsFilter === 'all' ? 'bold' : 'normal'
            }}
          >
            Всё время
          </button>
          <button
            onClick={() => {
              setStatsFilter('day');
              const today = new Date();
              const year = today.getFullYear();
              const month = String(today.getMonth() + 1).padStart(2, '0');
              const day = String(today.getDate()).padStart(2, '0');
              const todayStr = `${year}-${month}-${day}`;
              setStatsDateFrom(todayStr);
              setStatsDateTo(todayStr);
            }}
            style={{
              padding: '8px 16px',
              background: statsFilter === 'day' ? '#3b82f6' : 'white',
              color: statsFilter === 'day' ? 'white' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: statsFilter === 'day' ? 'bold' : 'normal'
            }}
          >
            Сегодня
          </button>
          <button
            onClick={() => {
              setStatsFilter('month');
              const today = new Date();
              const year = today.getFullYear();
              const month = String(today.getMonth() + 1).padStart(2, '0');
              
              const firstDay = `${year}-${month}-01`;
              const lastDay = new Date(year, today.getMonth() + 1, 0).getDate();
              const lastDayStr = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
              
              setStatsDateFrom(firstDay);
              setStatsDateTo(lastDayStr);
            }}
            style={{
              padding: '8px 16px',
              background: statsFilter === 'month' ? '#3b82f6' : 'white',
              color: statsFilter === 'month' ? 'white' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: statsFilter === 'month' ? 'bold' : 'normal'
            }}
          >
            Месяц
          </button>
          <button
            onClick={() => {
              setStatsFilter('year');
              const today = new Date();
              const year = today.getFullYear();
              
              setStatsDateFrom(`${year}-01-01`);
              setStatsDateTo(`${year}-12-31`);
            }}
            style={{
              padding: '8px 16px',
              background: statsFilter === 'year' ? '#3b82f6' : 'white',
              color: statsFilter === 'year' ? 'white' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: statsFilter === 'year' ? 'bold' : 'normal'
            }}
          >
            Год
          </button>
          <button
            onClick={() => setStatsFilter('custom')}
            style={{
              padding: '8px 16px',
              background: statsFilter === 'custom' ? '#3b82f6' : 'white',
              color: statsFilter === 'custom' ? 'white' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: statsFilter === 'custom' ? 'bold' : 'normal'
            }}
          >
            Свой период
          </button>
        </div>
        
        {statsFilter === 'custom' && (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <label style={{ fontSize: '14px' }}>С:</label>
            <input
              type="date"
              value={statsDateFrom}
              onChange={(e) => setStatsDateFrom(e.target.value)}
              style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
            <label style={{ fontSize: '14px' }}>По:</label>
            <input
              type="date"
              value={statsDateTo}
              onChange={(e) => setStatsDateTo(e.target.value)}
              style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>
        )}
      </div>

      {/* Заголовок с количеством дел */}
      <div style={{
        background: '#f9fafb',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '30px',
        textAlign: 'center'
      }}>
        <h2 style={{ margin: 0, fontSize: '28px', color: '#1f2937' }}>
          Выполнено дел: {totalTasks}
        </h2>
        <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
          {statsFilter === 'all' && 'За всё время'}
          {statsFilter === 'day' && 'Сегодня'}
          {statsFilter === 'month' && 'В текущем месяце'}
          {statsFilter === 'year' && 'В текущем году'}
          {statsFilter === 'custom' && `С ${statsDateFrom} по ${statsDateTo}`}
        </p>
      </div>

      {/* Контейнер для графиков */}
      <div style={{
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '30px',
        marginBottom: '30px'
      }}>
        <div style={{ display: 'flex', gap: '30px' }}>
          {/* График выполнения по времени - 75% ширины */}
          <div style={{ flex: '0 0 75%' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', color: '#1f2937' }}>
              📊 Динамика выполнения дел
            </h3>
          
            {allPeriods.length > 0 ? (
              <div style={{ position: 'relative', height: '400px', paddingLeft: '50px', paddingBottom: '40px' }}>
                <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
                  {/* Сетка и метки по оси Y */}
                  {[...Array(6)].map((_, i) => {
                    const value = Math.ceil(maxCount / 5) * (5 - i);
                    const y = (i * 360) / 5;
                    return (
                      <g key={i}>
                        <line
                          x1="0"
                          y1={y}
                          x2="100%"
                          y2={y}
                          stroke="#e5e7eb"
                          strokeWidth="1"
                          strokeDasharray={i === 5 ? "0" : "4 4"}
                        />
                        <text
                          x="-10"
                          y={y + 5}
                          textAnchor="end"
                          fontSize="12"
                          fill="#6b7280"
                        >
                          {value}
                        </text>
                      </g>
                    );
                  })}
                  
                  {/* Градиент для столбиков */}
                  <defs>
                    <linearGradient id="barGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#60a5fa" />
                      <stop offset="100%" stopColor="#3b82f6" />
                    </linearGradient>
                  </defs>
                  
                  {/* Столбики графика */}
                  {allPeriods.map((periodKey, idx) => {
                    const count = tasksByPeriod[periodKey];
                    const barWidth = 100 / allPeriods.length * 0.7;
                    const x = (idx / allPeriods.length) * 100 + (100 / allPeriods.length * 0.15);
                    const barHeight = (count / maxCount) * 360;
                    const y = 360 - barHeight;
                    
                    return (
                      <rect
                        key={`bar-${periodKey}`}
                        x={`${x}%`}
                        y={y}
                        width={`${barWidth}%`}
                        height={barHeight}
                        fill="url(#barGradient)"
                        rx="4"
                        ry="4"
                      />
                    );
                  })}
                  
                  {/* Метки и значения для столбиков */}
                  {allPeriods.map((periodKey, idx) => {
                    const count = tasksByPeriod[periodKey];
                    const barWidth = 100 / allPeriods.length * 0.7;
                    const x = (idx / allPeriods.length) * 100 + (100 / allPeriods.length * 0.5);
                    const barHeight = (count / maxCount) * 360;
                    const y = 360 - barHeight;
                    
                    let periodLabel: string;
                    let showLabel = true;
                    
                    if (dateFormat === 'hour') {
                      const hour = parseInt(periodKey.split('-')[3]);
                      periodLabel = `${hour}:00`;
                      showLabel = hour % 3 === 0;
                    } else if (dateFormat === 'day') {
                      const parts = periodKey.split('-');
                      const day = parseInt(parts[2]);
                      periodLabel = String(day);
                      showLabel = true;
                    } else if (dateFormat === 'month') {
                      const month = parseInt(periodKey.split('-')[1]);
                      const monthNames = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
                      periodLabel = monthNames[month - 1];
                    } else {
                      periodLabel = periodKey;
                    }
                    
                    return (
                      <g key={`label-${periodKey}`}>
                        {showLabel && (
                          <text
                            x={`${x}%`}
                            y="390"
                            textAnchor="middle"
                            fontSize="10"
                            fill="#6b7280"
                          >
                            {periodLabel}
                          </text>
                        )}
                        {count > 0 && (
                          <text
                            x={`${x}%`}
                            y={y - 8}
                            textAnchor="middle"
                            fontSize="11"
                            fontWeight="bold"
                            fill="#1f2937"
                          >
                            {count}
                          </text>
                        )}
                      </g>
                    );
                  })}
                </svg>
              </div>
            ) : (
              <div style={{
                padding: '60px',
                textAlign: 'center',
                color: '#9ca3af',
                fontSize: '16px'
              }}>
                📭 Нет выполненных дел за выбранный период
              </div>
            )}
          </div>

          {/* Круговая диаграмма по категориям - 25% ширины */}
          <div style={{
            flex: '0 0 25%',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#1f2937' }}>
              🎨 Категории
            </h3>
        
            {categoriesArray.length > 0 ? (
              <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
                {/* Круговая диаграмма */}
                <div style={{ maxWidth: '200px', margin: '0 auto' }}>
                  <svg viewBox="0 0 200 200" style={{ width: '100%', height: 'auto', maxWidth: '200px' }}>
                    {(() => {
                      let currentAngle = 0;
                      return categoriesArray.map((cat, idx) => {
                        const percentage = (cat.count / totalTasks) * 100;
                        const angle = (cat.count / totalTasks) * 360;
                        
                        const startAngle = currentAngle;
                        const endAngle = currentAngle + angle;
                        
                        const startRad = (startAngle - 90) * (Math.PI / 180);
                        const endRad = (endAngle - 90) * (Math.PI / 180);
                        
                        const x1 = 100 + 80 * Math.cos(startRad);
                        const y1 = 100 + 80 * Math.sin(startRad);
                        const x2 = 100 + 80 * Math.cos(endRad);
                        const y2 = 100 + 80 * Math.sin(endRad);
                        
                        const largeArc = angle > 180 ? 1 : 0;
                        
                        const pathData = [
                          `M 100 100`,
                          `L ${x1} ${y1}`,
                          `A 80 80 0 ${largeArc} 1 ${x2} ${y2}`,
                          `Z`
                        ].join(' ');
                        
                        const midAngle = startAngle + angle / 2;
                        const midRad = (midAngle - 90) * (Math.PI / 180);
                        const textX = 100 + 60 * Math.cos(midRad);
                        const textY = 100 + 60 * Math.sin(midRad);
                        
                        currentAngle += angle;
                        
                        return (
                          <g key={`cat-${idx}`}>
                            <path
                              d={pathData}
                              fill={cat.color}
                              opacity="0.9"
                            />
                            {percentage >= 10 && (
                              <text
                                x={textX}
                                y={textY}
                                textAnchor="middle"
                                fontSize="12"
                                fontWeight="bold"
                                fill="white"
                              >
                                {Math.round(percentage)}%
                              </text>
                            )}
                          </g>
                        );
                      });
                    })()}
                  </svg>
                </div>
                
                {/* Легенда категорий */}
                <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {categoriesArray.map((cat, idx) => (
                    <div key={`legend-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                      <div style={{ width: '16px', height: '16px', borderRadius: '3px', background: cat.color, flexShrink: 0 }}></div>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {cat.icon} {cat.name}
                      </span>
                      <span style={{ fontWeight: 'bold', color: '#6b7280', flexShrink: 0 }}>{cat.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#9ca3af',
                fontSize: '14px'
              }}>
                📭 Нет данных
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsTab;
